import React, {useState, createContext, useContext, useRef, useEffect} from "react";
import {MODAL_CLOSE_MS} from "@/utils/modalAnim.js";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {tripService} from "@/services/tripService";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {customersApiClient} from "@/services/apiClient";
import {bigClose} from "@img/index.js";
import {focusFromProxy} from "@/utils/iosFocusProxy";

// Ported from plugin customComponent/couponForm.jsx, using the dashboard's
// discount-code validation endpoint (mirrors applyCoupon.jsx).
const couponService = {
    async validateCoupon(code, amount) {
        if (!code) return {isValid: false, error: 'Coupon required', data: null};
        const result = await customersApiClient.postJson('/config/discount-codes/validate', {
            code,
            amount: Number(amount) || 0,
        });
        if (!result.success) {
            const fieldErr = result.raw?.errors?.code?.[0]
                || result.raw?.errors?.discount_code?.[0]
                || result.raw?.message
                || result.error
                || 'The provided discount code is invalid.';
            return {isValid: false, error: fieldErr, data: null, raw: result.raw};
        }
        return {isValid: true, error: null, data: result.data};
    },
};

const CouponFormContext = createContext(null);

export function CouponForm({children, ...props}) {
    const couponKey = props.couponKey || STORAGE_KEYS.COUPON_VALUE;
    const couponDataKey = STORAGE_KEYS.COUPON_DATA;

    const [isApplying, setIsApplying] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [couponValue, setCouponValue] = useState(
        storage.get(couponKey, "") || props.defaultCouponCode || ""
    );
    // The code currently APPLIED to the booking — which is not the same thing as what
    // is in the box. Editing the text has to be able to undo the discount, so we need
    // to know whether there is a discount live on the price right now.
    const [appliedCoupon, setAppliedCoupon] = useState(
        storage.get(couponKey, "") || props.defaultCouponCode || ""
    );

    const resetMessages = () => {
        setError("");
        setSuccess("");
    };

    // Take the applied coupon back OFF the booking and reprice without it. Leaves the
    // text box alone — the caller decides whether the code stays on screen (typing a
    // correction) or goes (the ✕).
    const unapplyCoupon = async () => {
        if (!appliedCoupon) return;      // nothing live — nothing to undo
        setAppliedCoupon("");
        setIsRemoving(true);
        try {
            storage.remove(couponKey);
            storage.remove(couponDataKey);
            await tripService.recalculateCost();
        } catch (err) {
            // The discount is already off the stored booking; a failed reprice only
            // means the on-screen total is stale, and any later apply/submit repeats it.
            console.error("Coupon removal failed:", err);
        } finally {
            setIsRemoving(false);
        }
    };

    // Typing in the box. A code that no longer matches the applied one can't keep
    // discounting the price, so the discount comes off as soon as it diverges —
    // otherwise the passenger sees a total that no code on screen explains.
    const changeCouponValue = (next) => {
        setCouponValue(next);
        resetMessages();
        if (appliedCoupon && next.trim().toUpperCase() !== appliedCoupon.trim().toUpperCase()) {
            unapplyCoupon();
        }
    };

    // The ✕: empty the box AND drop the coupon.
    const clearCoupon = () => {
        setCouponValue("");
        resetMessages();
        unapplyCoupon();
    };

    async function applyCouponForm(e) {
        if (e?.preventDefault) e.preventDefault();

        resetMessages();
        setIsApplying(true);

        const value = couponValue.trim();
        if (!value) {
            setError("Please enter a coupon code.");
            setIsApplying(false);
            return;
        }

        try {
            // total_price is stored in cents; the endpoint expects a dollar amount.
            const totalCents = props.carDetails?.total_price ?? 0;
            const totalAmount = totalCents / 100;
            const couponResult = await couponService.validateCoupon(value, totalAmount);

            if (!couponResult.isValid) {
                storage.remove(couponKey);
                storage.remove(couponDataKey);
                setAppliedCoupon("");
                setError(couponResult.error || "Invalid or expired coupon.");
                setIsApplying(false);
                return;
            }

            storage.set(couponKey, value);
            storage.set(couponDataKey, couponResult.data);

            const carsResult = await tripService.recalculateCost();
            if (!carsResult.success) {
                storage.remove(couponKey);
                storage.remove(couponDataKey);
                setAppliedCoupon("");
                setError("Failed to recalculate prices. Please try again.");
                setIsApplying(false);
                return;
            }

            setAppliedCoupon(value);
            setSuccess("Coupon applied successfully.");

            setTimeout(function () {
                if (props.setStep && props.previousStep) {
                    props.setStep(props.previousStep);
                } else if (props.setApplyCoupon) {
                    props.setApplyCoupon(false);
                }
            }, 1000);
        } catch (err) {
            console.error("Coupon check failed:", err);
            setError("Something went wrong. Please try again.");
        } finally {
            setIsApplying(false);
        }
    }

    // The code in the box is already live on the booking, so there is nothing left to
    // apply. Editing the text unapplies it (see changeCouponValue), which clears
    // appliedCoupon and re-enables the button — so this only stays true while the two
    // actually match.
    const isApplied = !!appliedCoupon
        && couponValue.trim().toUpperCase() === appliedCoupon.trim().toUpperCase();

    const value = {
        isApplying,
        isRemoving,
        isApplied,
        error,
        success,
        couponValue,
        setCouponValue,
        changeCouponValue,
        clearCoupon,
        resetMessages,
        applyCouponForm,
    };

    if (children) {
        return (
            <CouponFormContext.Provider value={value}>
                {children}
            </CouponFormContext.Provider>
        );
    }

    return (
        <CouponFormContext.Provider value={value}>
            <form onSubmit={applyCouponForm} className="coupon-form">
                <CouponFormInput/>
                <CouponFormSubmit/>
            </form>
        </CouponFormContext.Provider>
    );
}

export function CouponFormInput() {
    const ctx = useContext(CouponFormContext);
    const inputRef = useRef(null);

    /**
     * Focus is DEFERRED past the sheet's entrance instead of using `autoFocus`.
     *
     * On mobile this input lives in a nested modal that slides up over 0.5s. Focusing
     * on mount opens the keyboard immediately, and the visual-viewport handler then
     * re-anchors `.hopn-modal-overlay` (--hopn-vvh / --hopn-vv-top) WHILE the slide-up
     * is still running — the sheet visibly jumps downward mid-animation, which reads
     * as a broken top-to-bottom entrance.
     *
     * On iOS a deferred focus gets the caret but no keyboard; the tap that opened the
     * applyCoupon step primed a proxy input (hopnModal smartSetStep → utils/iosFocusProxy)
     * and focusFromProxy hands the keyboard over to this field.
     */
    useEffect(() => {
        if (typeof window !== 'undefined' && window.innerWidth >= 576) {
            inputRef.current?.focus();
            return;
        }
        const t = setTimeout(() => focusFromProxy(() => inputRef.current), MODAL_CLOSE_MS);
        return () => clearTimeout(t);
    }, []);

    if (!ctx) return null;
    const {couponValue, changeCouponValue, clearCoupon, isApplied, error, success, applyCouponForm} = ctx;
    return (
        <div className="coupon-form">
            <div className="coupon-form-field">
                <input
                    ref={inputRef}
                    value={couponValue}
                    name="hopn-coupon"
                    placeholder="Coupon Code..."
                    onChange={(e) => changeCouponValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") applyCouponForm();
                    }}
                />
                {/* Only while a coupon is actually APPLIED — the button removes a live
                    discount, so there is nothing for it to do next to a code still being
                    typed. (Editing the code unapplies it, which hides this again.) */}
                {isApplied && (
                    <button
                        type="button"
                        className="coupon-form-clear"
                        aria-label="Remove coupon"
                        onClick={clearCoupon}
                    >
                        {/* The same cross the modal header's close uses, so the two
                            crosses on this popup are the one glyph. */}
                        <img src={bigClose} alt=""/>
                    </button>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
        </div>
    );
}

export function CouponFormSubmit({className = "hopn-add-passenger-button"}) {
    const ctx = useContext(CouponFormContext);
    if (!ctx) return null;
    const {isApplying, isRemoving, isApplied, applyCouponForm} = ctx;
    return (
        <button
            type="button"
            onClick={applyCouponForm}
            // Disabled while the code on screen is already applied (nothing to do), and
            // while a removal is in flight — that is repricing the booking, and applying
            // on top of it would race the two recalculations.
            disabled={isApplying || isRemoving || isApplied}
            className={className}
        >
            {isApplying ? (
                <Loader color="#fff"/>
            ) : (
                "Apply Coupon"
            )}
        </button>
    );
}
